import React, { useEffect, useMemo, useState } from 'react';
import { X, Calendar, CreditCard, User as UserIcon, Home } from 'lucide-react';
import type { Room, Resident } from '../types';
import type { User } from '../types/auth';
import { calculateNextPaymentDate, calculateNextDueDateAnchored } from '../utils/dates';
import { formatDateDDMMYYYY } from '../utils/format';
import {
  addMonthsToDate,
  getOpenCicilanSisa,
  isCicilanSisaOpen,
  suggestedCicilanNumber,
} from '../utils/cicilan';

export type PaymentCategory = 'DP' | 'Pelunasan' | 'Cicilan';

interface UnifiedTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  rooms: Room[];
  currentUser?: User;
  onAddResident: (
    roomNumber: string,
    position: 1 | 2,
    residentData: {
      name: string;
      phone: string;
      study: string;
      startDate: string;
      paymentPattern: number;
      paymentProofDate?: string;
      paymentProofLink?: string;
      nextPaymentDate?: string;
      category?: PaymentCategory;
      installmentNumber?: number;
    }
  ) => Promise<boolean>;
  onAddPayment: (
    roomNumber: string,
    residentPosition: 1 | 2,
    periodData: {
      periodNumber: number;
      paymentProofDate?: string;
      paymentProofLink?: string;
      nextPaymentDate?: string;
      category?: PaymentCategory;
      installmentNumber?: number;
    }
  ) => Promise<boolean>;
}

export function UnifiedTransactionModal({ isOpen, onClose, rooms, currentUser, onAddResident, onAddPayment }: UnifiedTransactionModalProps) {
  const [mode, setMode] = useState<'Lama' | 'Baru'>('Lama');
  const [selectedBuilding, setSelectedBuilding] = useState<'gajah4' | 'gajah3' | 'anduring' | 'limau_manis'>('gajah4');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedPosition, setSelectedPosition] = useState<1 | 2 | ''>('');
  const [category, setCategory] = useState<PaymentCategory | ''>('');
  const [installmentNumber, setInstallmentNumber] = useState<number | ''>('');
  const [periodNumber, setPeriodNumber] = useState<number>(1);
  const [paymentProofDate, setPaymentProofDate] = useState('');
  const [nextPaymentDate, setNextPaymentDate] = useState('');
  const [paymentProofLink, setPaymentProofLink] = useState('');
  const [existingPaymentPattern, setExistingPaymentPattern] = useState<number | ''>('');
  const canUseInstallments = !!(
    currentUser && ['yudha', 'irfan', 'oeoel'].includes(currentUser.username)
  );

  // Filter rooms based on user role and selected building
  const filteredRooms = useMemo(() => {
    let roomsToShow = rooms;
    
    if (currentUser?.role === 'resident') {
      // For residents, only show their own room
      if (currentUser.roomNumber) {
        roomsToShow = rooms.filter(room => room.number === currentUser.roomNumber);
      } else {
        roomsToShow = [];
      }
      } else {
        // For admin users, filter by building type and selected building
        const isAdmin = currentUser && (currentUser.username === 'yudha' || currentUser.username === 'irfan' || currentUser.username === 'oeoel');
        if (isAdmin) {
          roomsToShow = rooms.filter(room => 
            room.buildingType === selectedBuilding || 
            (!room.buildingType && selectedBuilding === 'gajah4')
          );
        } else {
          // For other users, only show gajah4
          roomsToShow = rooms.filter(room => 
            room.buildingType === 'gajah4' || !room.buildingType
          );
        }
      }    return roomsToShow;
  }, [rooms, currentUser, selectedBuilding]);

  // Helper function to determine user position from username
  const getUserPosition = (username: string): 1 | 2 => {
    return username.endsWith('bb') ? 2 : 1;
  };

  // Get available positions based on current user
  const getAvailablePositions = () => {
    if (!currentUser || currentUser.role !== 'resident') {
      return [1, 2]; // Non-residents can access all positions
    }
    
    // For residents, only show their own position
    const userPosition = getUserPosition(currentUser.username);
    return [userPosition];
  };

  const availablePositions = getAvailablePositions();

  // Resident fields for new resident
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newStudy, setNewStudy] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newPaymentPattern, setNewPaymentPattern] = useState<number>(1);

  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedRoomData = useMemo(() => filteredRooms.find(r => r.number === selectedRoom), [filteredRooms, selectedRoom]);
  const selectedResident: Resident | undefined = useMemo(() => {
    if (!selectedRoomData || !selectedPosition) return undefined;
    return selectedRoomData.residents.find(r => r.position === selectedPosition);
  }, [selectedRoomData, selectedPosition]);
  const openCicilanSisa = useMemo(
    () => (selectedResident ? getOpenCicilanSisa(selectedResident) : null),
    [selectedResident]
  );
  const cicilanSisaDuePreview =
    category === 'Cicilan' && Number(installmentNumber) === 1 && paymentProofDate
      ? addMonthsToDate(paymentProofDate, 1)
      : '';

  // Auto-derive periodNumber and default pattern for existing resident
  useEffect(() => {
    if (mode === 'Lama' && selectedResident) {
      const latest = selectedResident.paymentPeriods?.reduce((max, p) => p.periodNumber > max ? p.periodNumber : max, 0) || 0;
      setPeriodNumber(latest || 1);
      setExistingPaymentPattern(selectedResident.paymentPattern || 1);
    }
  }, [mode, selectedResident]);

  // Suggest cicilan number when switching to Cicilan for existing resident
  useEffect(() => {
    if (category === 'Cicilan' && mode === 'Lama' && selectedResident) {
      setInstallmentNumber(suggestedCicilanNumber(selectedResident));
    }
  }, [category, mode, selectedResident]);

  // Auto-compute next payment date based on mode/inputs
  useEffect(() => {
    if (mode === 'Lama' && selectedResident) {
      const installment = Number(installmentNumber || 0);
      // Cicilan ke-2+: keep existing period due (do not re-anchor off sisa payment date)
      if (category === 'Cicilan' && installment >= 2) {
        const openSisa = getOpenCicilanSisa(selectedResident);
        const preserved =
          selectedResident.nextPaymentDate ||
          openSisa?.periodDueDate ||
          '';
        if (preserved) {
          setNextPaymentDate(preserved);
          return;
        }
      }

      if (paymentProofDate) {
        const pattern = (existingPaymentPattern || selectedResident.paymentPattern || 1) as number;
        const computed = calculateNextDueDateAnchored(selectedResident.startDate || paymentProofDate, pattern, paymentProofDate);
        setNextPaymentDate(computed);
      } else {
        const base = selectedResident.nextPaymentDate || calculateNextPaymentDate(selectedResident.startDate || new Date().toISOString().slice(0,10), selectedResident.paymentPattern || 1);
        setNextPaymentDate(base);
      }
    }
    if (mode === 'Baru') {
      if (newStartDate) {
        const computed = calculateNextPaymentDate(newStartDate, newPaymentPattern || 1);
        setNextPaymentDate(computed);
      } else if (paymentProofDate) {
        const computed = calculateNextPaymentDate(paymentProofDate, newPaymentPattern || 1);
        setNextPaymentDate(computed);
      }
    }
  }, [paymentProofDate, mode, selectedResident, newPaymentPattern, newStartDate, existingPaymentPattern, category, installmentNumber]);

  // Reset form when switching modes or closing
  const resetAll = () => {
    setSelectedBuilding('gajah4');
    setSelectedRoom('');
    setSelectedPosition('');
    setCategory('');
    setInstallmentNumber('');
    setPeriodNumber(1);
    setPaymentProofDate('');
    setNextPaymentDate('');
    setPaymentProofLink('');
    setNewName('');
    setNewPhone('');
    setNewStudy('');
    setNewStartDate('');
    setNewPaymentPattern(1);
    setValidationError('');
  };

  const handleClose = () => { resetAll(); onClose(); };

  useEffect(() => {
    if (!canUseInstallments && category === 'Cicilan') {
      setCategory('');
      setInstallmentNumber('');
    }
  }, [canUseInstallments, category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!selectedRoom) { setValidationError('Pilih kamar.'); return; }

    if (mode === 'Lama') {
      if (!selectedPosition) { setValidationError('Pilih penghuni.'); return; }
      if (!category) { setValidationError('Pilih kategori.'); return; }
      if (category === 'Cicilan' && !canUseInstallments) { setValidationError('Cicilan hanya dapat diinput oleh admin.'); return; }
      if (category === 'Cicilan' && (!installmentNumber || Number(installmentNumber) < 1)) { setValidationError('Nomor cicilan harus diisi dan >= 1.'); return; }
      if (!paymentProofDate) { setValidationError('Tanggal bukti bayar wajib.'); return; }

      if (category === 'Cicilan' && selectedResident) {
        const openSisa = isCicilanSisaOpen(selectedResident);
        const n = Number(installmentNumber);
        if (n === 1 && openSisa) {
          setValidationError('Masih ada sisa cicilan terbuka. Gunakan Cicilan ke-2 atau Pelunasan untuk menutup sisa.');
          return;
        }
        if (n >= 2 && !openSisa) {
          setValidationError('Belum ada Cicilan ke-1 yang terbuka. Input Cicilan ke-1 terlebih dahulu.');
          return;
        }
      }

      // If category is Pelunasan, next periodNumber should increment; otherwise keep same period
      const finalPeriodNumber = category === 'Pelunasan' ? (periodNumber || 1) + 1 : (periodNumber || 1);

      setIsSubmitting(true);
      try {
        let nextDate = nextPaymentDate;
        const installment = Number(installmentNumber || 0);
        if (category === 'Cicilan' && installment >= 2 && selectedResident) {
          const openSisa = getOpenCicilanSisa(selectedResident);
          nextDate =
            nextPaymentDate ||
            selectedResident.nextPaymentDate ||
            openSisa?.periodDueDate ||
            '';
        } else if (!nextDate && selectedResident) {
          const pattern = (existingPaymentPattern || selectedResident.paymentPattern || 1) as number;
          nextDate = calculateNextDueDateAnchored(selectedResident.startDate || paymentProofDate, pattern, paymentProofDate);
        }
        const ok = await onAddPayment(
          selectedRoom,
          selectedPosition as 1 | 2,
          {
            periodNumber: finalPeriodNumber,
            paymentProofDate,
            paymentProofLink: paymentProofLink || undefined,
            nextPaymentDate: nextDate,
            category: category as PaymentCategory,
            installmentNumber: category === 'Cicilan' ? Number(installmentNumber) : undefined
          }
        );
        if (ok) handleClose();
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // New resident flow
    if (!selectedPosition) { setValidationError('Pilih posisi penghuni (1/2).'); return; }
    if (!newName.trim()) { setValidationError('Nama penghuni wajib.'); return; }
    if (!newStartDate) { setValidationError('Tanggal masuk wajib.'); return; }
    if (!category) { setValidationError('Pilih kategori.'); return; }
    if (category === 'Cicilan' && !canUseInstallments) { setValidationError('Cicilan hanya dapat diinput oleh admin.'); return; }
    if (category === 'Cicilan' && (!installmentNumber || Number(installmentNumber) < 1)) { setValidationError('Nomor cicilan harus diisi dan >= 1.'); return; }

    setIsSubmitting(true);
    try {
      let nextDate = nextPaymentDate;
      if (!nextDate && newStartDate) {
        nextDate = calculateNextPaymentDate(newStartDate, newPaymentPattern || 1);
      }
      const ok = await onAddResident(
        selectedRoom,
        selectedPosition as 1 | 2,
        {
          name: newName.trim(),
          phone: newPhone,
          study: newStudy,
          startDate: newStartDate,
          paymentPattern: newPaymentPattern,
          paymentProofDate,
          paymentProofLink: paymentProofLink || undefined,
          nextPaymentDate: nextDate,
          category,
          installmentNumber: category === 'Cicilan' ? Number(installmentNumber) : undefined
        }
      );
      if (ok) handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const availableRooms = [...filteredRooms].sort((a, b) => parseInt(a.number) - parseInt(b.number));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Transaksi: Penghuni Lama / Baru
          </h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Mode */}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setMode('Lama')} className={`p-2 rounded border ${mode==='Lama' ? 'btn-primary' : 'bg-gray-50 dark:bg-gray-800'}`}>Penghuni Lama</button>
            <button type="button" onClick={() => setMode('Baru')} className={`p-2 rounded border ${mode==='Baru' ? 'btn-primary' : 'bg-gray-50 dark:bg-gray-800'}`}>Penghuni Baru</button>
          </div>

          {/* Building Selection - Only show for admin users */}
          {currentUser && (currentUser.username === 'yudha' || currentUser.username === 'irfan' || currentUser.username === 'oeoel') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Home className="inline h-4 w-4 mr-1" />Pilih Kos
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  type="button" 
                  onClick={() => {
                    setSelectedBuilding('gajah4');
                    setSelectedRoom('');
                    setSelectedPosition('');
                  }} 
                  className={`p-2 rounded border text-sm ${selectedBuilding==='gajah4' ? 'bg-green-600 text-white' : 'bg-gray-50 dark:bg-gray-800'}`}
                >
                  Kos Gajah 4
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setSelectedBuilding('gajah3');
                    setSelectedRoom('');
                    setSelectedPosition('');
                  }} 
                  className={`p-2 rounded border text-sm ${selectedBuilding==='gajah3' ? 'bg-green-600 text-white' : 'bg-gray-50 dark:bg-gray-800'}`}
                >
                  Kos Gajah 3
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setSelectedBuilding('anduring');
                    setSelectedRoom('');
                    setSelectedPosition('');
                  }} 
                  className={`p-2 rounded border text-sm ${selectedBuilding==='anduring' ? 'bg-green-600 text-white' : 'bg-gray-50 dark:bg-gray-800'}`}
                >
                  Kos Anduring
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setSelectedBuilding('limau_manis');
                    setSelectedRoom('');
                    setSelectedPosition('');
                  }} 
                  className={`p-2 rounded border text-sm ${selectedBuilding==='limau_manis' ? 'bg-green-600 text-white' : 'bg-gray-50 dark:bg-gray-800'}`}
                >
                  Kos Limau Manis
                </button>
              </div>
            </div>
          )}

          {/* Room */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"><Home className="inline h-4 w-4 mr-1" />Pilih Kamar</label>
            <select value={selectedRoom} onChange={(e) => { setSelectedRoom(e.target.value); setSelectedPosition(''); }} className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800">
              <option value="">-- Pilih Kamar --</option>
              {availableRooms.map(room => (
                <option key={room.number} value={room.number}>Kamar {room.number}</option>
              ))}
            </select>
          </div>

          {/* Position / Resident */}
          {selectedRoom && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"><UserIcon className="inline h-4 w-4 mr-1" />Pilih Penghuni</label>
              <select value={selectedPosition} onChange={e => setSelectedPosition(e.target.value ? (parseInt(e.target.value) as 1 | 2) : '')} className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800">
                <option value="">-- Pilih Posisi --</option>
                {availablePositions.map(p => (
                  <option key={p} value={p}>Posisi {p} {selectedRoomData?.residents.find(r => r.position===p)?.name ? `- ${selectedRoomData?.residents.find(r => r.position===p)?.name}` : ''}</option>
                ))}
              </select>
            </div>
          )}

          {/* New resident fields */}
          {mode==='Baru' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nama</label>
                <input value={newName} onChange={e=>setNewName(e.target.value)} className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">No. HP</label>
                <input value={newPhone} onChange={e=>setNewPhone(e.target.value)} className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Program Studi</label>
                <input value={newStudy} onChange={e=>setNewStudy(e.target.value)} className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tanggal Masuk</label>
                <input type="date" value={newStartDate} onChange={e=>setNewStartDate(e.target.value)} className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pola Pembayaran</label>
                <select value={newPaymentPattern} onChange={e=>setNewPaymentPattern(parseInt(e.target.value)||1)} className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800">
                  {[...Array(12)].map((_,i)=>i+1).map(m=> (
                    <option key={m} value={m}>Per {m} bulan</option>
                  ))}
                  <option value={24}>Per 2 tahun</option>
                  <option value={36}>Per 3 tahun</option>
                </select>
              </div>
            </div>
          )}

          {/* Category */}
          {selectedPosition && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kategori Pembayaran</label>
              <select value={category} onChange={e=>setCategory(e.target.value as PaymentCategory)} className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800">
                <option value="">-- Pilih Kategori --</option>
                <option value="DP">DP</option>
                {canUseInstallments && <option value="Cicilan">Cicilan</option>}
                <option value="Pelunasan">Pelunasan</option>
              </select>
            </div>
          )}

          {/* Existing resident period context */}
          {mode==='Lama' && selectedResident && (
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-sm">
              <p className="text-gray-700 dark:text-gray-300">
                Periode terakhir: {selectedResident.paymentPeriods?.length || 0}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Jatuh tempo saat ini: {formatDateDDMMYYYY(selectedResident.nextPaymentDate)}
              </p>
              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pola Pembayaran</label>
                <select
                  value={existingPaymentPattern}
                  onChange={e=>setExistingPaymentPattern(e.target.value ? Number(e.target.value) : '')}
                  className="w-full p-2 border rounded bg-white dark:bg-gray-800"
                >
                  <option value="">Ikuti data penghuni ({selectedResident.paymentPattern} bln)</option>
                  {[...Array(12)].map((_,i)=>i+1).map(m=> (
                    <option key={m} value={m}>Per {m} bulan</option>
                  ))}
                  <option value={24}>Per 2 tahun</option>
                  <option value={36}>Per 3 tahun</option>
                </select>
              </div>
            </div>
          )}

          {/* Cicilan number + dual-due helper */}
          {category==='Cicilan' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cicilan ke-</label>
                <input type="number" min={1} max={2} value={installmentNumber} onChange={e=>setInstallmentNumber(e.target.value ? Number(e.target.value) : '')} className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800" />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Biasanya 2 kali: Cicilan 1 membuka sisa, Cicilan 2 (atau Pelunasan) menutup sisa.
                </p>
              </div>
              {Number(installmentNumber) === 1 && paymentProofDate && cicilanSisaDuePreview && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm space-y-1">
                  <p className="font-semibold text-amber-800 dark:text-amber-300">Jadwal cicilan (otomatis)</p>
                  <p className="text-amber-800 dark:text-amber-200">
                    Jatuh tempo sisa cicilan: <strong>{formatDateDDMMYYYY(cicilanSisaDuePreview)}</strong> (+1 bulan)
                  </p>
                  <p className="text-amber-800 dark:text-amber-200">
                    Jatuh tempo periode berikutnya: <strong>{formatDateDDMMYYYY(nextPaymentDate)}</strong> (sesuai pola sewa)
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Sisa akan tampil di panel &quot;Sisa Cicilan Belum Dilunasi&quot; sampai Cicilan 2 atau Pelunasan dicatat.
                  </p>
                </div>
              )}
              {Number(installmentNumber) >= 2 && openCicilanSisa && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3 text-sm space-y-1">
                  <p className="font-semibold text-green-800 dark:text-green-300">Menutup sisa cicilan</p>
                  <p className="text-green-800 dark:text-green-200">
                    Sisa due: <strong>{formatDateDDMMYYYY(openCicilanSisa.sisaDueDate)}</strong>
                  </p>
                  <p className="text-green-800 dark:text-green-200">
                    Periode berikutnya tetap: <strong>{formatDateDDMMYYYY(nextPaymentDate)}</strong>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Dates / link */}
          {selectedPosition && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={category === 'DP' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  {category === 'DP' ? 'Tanggal DP' : 'Tanggal Bukti Bayar'}
                </label>
                <div className="flex gap-2 items-center">
                  <input type="date" value={paymentProofDate} onChange={e=>setPaymentProofDate(e.target.value)} className="flex-1 p-3 border rounded-lg bg-white dark:bg-gray-800" required />
                  <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDateDDMMYYYY(paymentProofDate)}</div>
                </div>
              </div>
              {/* Hide Next Payment Date for DP category */}
              {category !== 'DP' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"><Calendar className="inline h-4 w-4 mr-1" />Tanggal Bayar Selanjutnya</label>
                  <div className="flex gap-2 items-center">
                    <input 
                      type="date" 
                      value={nextPaymentDate} 
                      onChange={e=>setNextPaymentDate(e.target.value)} 
                      className={`flex-1 p-3 border rounded-lg ${
                        currentUser?.role === 'resident' 
                          ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' 
                          : 'bg-white dark:bg-gray-800'
                      }`}
                      disabled={currentUser?.role === 'resident'}
                      readOnly={currentUser?.role === 'resident'}
                      required 
                    />
                    <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDateDDMMYYYY(nextPaymentDate)}</div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {currentUser?.role === 'resident' 
                      ? 'Tanggal ini dihitung otomatis berdasarkan pola pembayaran dan tidak dapat diubah.' 
                      : category === 'Cicilan' && Number(installmentNumber) >= 2
                        ? 'Cicilan ke-2: tanggal periode berikutnya dipertahankan (tidak digeser oleh bayar sisa).'
                        : category === 'Cicilan'
                          ? 'Cicilan ke-1: ini jatuh tempo periode sewa berikutnya (bukan due sisa +1 bulan).'
                          : 'Otomatis dari pola pembayaran. Penghuni Baru: dari tanggal masuk. Penghuni Lama: dari tanggal masuk, setelah bukti bayar.'
                    }
                  </p>
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Link Bukti Bayar</label>
                <input type="url" value={paymentProofLink} onChange={e=>setPaymentProofLink(e.target.value)} placeholder="https://contoh.com/bukti.jpg" className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800" />
              </div>
            </div>
          )}

          {/* Period number logic note */}
          {mode==='Lama' && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <CreditCard className="inline h-3 w-3 mr-1" />
              Nomor periode otomatis bertambah hanya saat kategori Pelunasan.
            </div>
          )}

          {/* Error */}
          {validationError && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {validationError}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={handleClose} className="flex-1 px-4 py-3 border rounded-lg">Batal</button>
            <button type="submit" disabled={isSubmitting || !selectedRoom || !selectedPosition || !paymentProofDate || !category} className="flex-1 px-4 py-3 btn-primary rounded-lg disabled:opacity-50">
              {isSubmitting ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
